import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles } from '@/utils/pageBranding'
import {
  ShoppingCart,
  Plus,
  ExternalLink,
  Edit,
  Eye,
  Globe,
  Webhook,
  CheckCircle,
  XCircle,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/DataTable'
import { SaleShop } from '@/types'
import { saleShopService } from '@/services/saleShopService'
import { formatDistanceToNow } from 'date-fns'
import { TableColumn, TableAction, TablePagination, TableSort, TableFilter } from '@/types/table'

export default function SaleShops() {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const [saleShops, setSaleShops] = useState<SaleShop[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [pagination, setPagination] = useState<TablePagination>({
    current: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [5, 10, 20, 50],
  })
  const [sort, setSort] = useState<TableSort | undefined>({
    key: 'created_at',
    direction: 'desc',
  })
  const [filters, setFilters] = useState<TableFilter[]>([])

  // Initial data fetch
  useEffect(() => {
    fetchSaleShops({
      page: pagination.current,
      pageSize: pagination.pageSize,
      sort,
      filters,
      search: '',
    })
  }, [])

  const fetchSaleShops = async (params: {
    page: number
    pageSize: number
    sort?: TableSort
    filters: TableFilter[]
    search?: string
  }) => {
    setLoading(true)
    setError(null)
    try {
      // Convert filters to API parameters
      const statusFilter = filters.find(f => f.key === 'is_active')
      const typeFilter = filters.find(f => f.key === 'type')

      const response = await saleShopService.getSaleShops({
        page: params.page,
        page_size: params.pageSize,
        search: params.search || undefined,
        is_active: statusFilter ? statusFilter.value : undefined,
        type: typeFilter ? typeFilter.value : undefined,
      })

      if (response.success) {
        setSaleShops(response.data.results)
        setTotalCount(response.data.count)
        setPagination(prev => ({
          ...prev,
          current: params.page,
          total: response.data.count,
        }))
        return { data: response.data.results, total: response.data.count }
      } else {
        setError(response.message || 'Failed to fetch sale shops')
        return { data: [], total: 0 }
      }
    } catch (error: any) {
      const errorMessage = saleShopService.handleError
        ? saleShopService.handleError(error)
        : 'Failed to fetch sale shops'
      setError(errorMessage)
      console.error('Failed to fetch sale shops:', error)
      return { data: [], total: 0 }
    } finally {
      setLoading(false)
    }
  }

  // Define table columns
  const columns: TableColumn<SaleShop>[] = [
    {
      key: 'name',
      title: 'Channel',
      dataIndex: 'name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (_, record) => (
        <div className="flex items-center space-x-3">
          <div
            className="p-2 rounded-lg"
            style={{
              backgroundColor: record.is_active ? `${branding.secondaryColor}20` : `${branding.textSecondaryColor}20`
            }}
          >
            <ShoppingCart
              className="h-5 w-5"
              style={{ color: record.is_active ? branding.secondaryColor : branding.textSecondaryColor }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" style={pageStyles.text}>{record.name}</div>
            {record.description && (
              <div className="max-w-xs text-sm truncate" style={pageStyles.textSecondary}>{record.description}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'is_active',
      title: 'Status',
      dataIndex: 'is_active',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false },
      ],
      render: value => (
        <span
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
          style={{
            backgroundColor: value ? `${branding.secondaryColor}20` : `${branding.textSecondaryColor}20`,
            color: value ? branding.secondaryColor : branding.textSecondaryColor,
            borderColor: value ? `${branding.secondaryColor}40` : `${branding.textSecondaryColor}40`
          }}
        >
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'currency_code',
      title: 'Currency',
      dataIndex: 'currency_code',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (value, record) =>
        value ? (
          <div className="flex items-center space-x-2">
            <span className="text-lg">💰</span>
            <div>
              <div className="text-sm font-medium" style={pageStyles.text}>{record.currency_code}</div>
              {record.currency_name && (
                <div className="text-xs truncate" style={pageStyles.textSecondary}>{record.currency_name}</div>
              )}
            </div>
          </div>
        ) : (
          <span className="text-sm" style={pageStyles.textSecondary}>No currency</span>
        ),
    },
    {
      key: 'website_url',
      title: 'Website',
      dataIndex: 'website_url',
      sortable: false,
      filterable: false,
      render: value =>
        value ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-1 text-sm hover:opacity-80"
            style={{ color: branding.primaryColor }}
          >
            <Globe className="flex-shrink-0 w-4 h-4" />
            <span className="truncate max-w-32">Visit Site</span>
            <ExternalLink className="flex-shrink-0 w-3 h-3" />
          </a>
        ) : (
          <span className="text-sm" style={pageStyles.textSecondary}>No website</span>
        ),
    },
    {
      key: 'created_at',
      title: 'Created',
      dataIndex: 'created_at',
      sortable: true,
      filterable: false,
      render: value => (
        <span className="text-sm" style={pageStyles.textSecondary}>
          {formatDistanceToNow(new Date(value), { addSuffix: true })}
        </span>
      ),
    },
  ]

  // Define table actions
  const actions: TableAction<SaleShop>[] = [
    {
      key: 'view',
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: record => {
        // Navigate to view page
        window.location.href = `/sale-shops/${record.id}`
      },
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: <Edit className="w-4 h-4" />,
      onClick: record => {
        // Navigate to edit page
        window.location.href = `/sale-shops/${record.id}`
      },
    },
  ]

  return (
    <div
      className="min-h-screen"
      style={pageStyles.container}
    >
      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              {/* <h1 className="text-4xl font-bold text-white">Sales Channels</h1> */}
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3">
              <Link to="/sale-shops/create">
                <Button className="flex items-center justify-center px-4 py-2 text-sm text-white bg-pexi-orange hover:bg-pexi-orange-dark sm:px-6 sm:py-3 sm:text-base">
                  <Plus className="w-4 h-4 mr-2 sm:h-5 sm:w-5" />
                  <span className="font-medium">Create Channel</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-8">
          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    Total Channels
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>{totalCount}</p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>All sales channels</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={pageStyles.primaryBg}>
                  <ShoppingCart className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    Active Channels
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>
                    {saleShops.filter(shop => shop.is_active).length}
                  </p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>Currently running</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={{ backgroundColor: branding.secondaryColor }}>
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    Inactive Channels
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>
                    {saleShops.filter(shop => !shop.is_active).length}
                  </p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>Paused or disabled</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={pageStyles.primaryBg}>
                  <XCircle className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    With Webhooks
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>
                    {saleShops.filter(shop => shop.webhook_url).length}
                  </p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>Real-time notifications</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={{ backgroundColor: branding.secondaryColor }}>
                  <Webhook className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-6 mb-8 transition-all duration-300 border card backdrop-blur-sm" style={cardStyles}>
            <div className="flex items-start space-x-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                <XCircle className="w-6 h-6" style={{ color: branding.primaryColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mb-1 text-lg font-semibold" style={pageStyles.text}>Error loading sale shops</h3>
                <p className="mb-4 text-sm" style={{ color: branding.primaryColor }}>{error}</p>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      fetchSaleShops({
                        page: pagination.current,
                        pageSize: pagination.pageSize,
                        sort,
                        filters,
                        search: '',
                      })
                    }
                    className="hover:opacity-80"
                    style={{
                      backgroundColor: `${branding.primaryColor}20`,
                      borderColor: `${branding.primaryColor}30`,
                      color: branding.primaryColor
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    className="hover:opacity-80"
                    style={{
                      color: branding.primaryColor,
                      backgroundColor: `${branding.primaryColor}20`
                    }}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
          <DataTable
            columns={columns}
            data={saleShops}
            loading={loading}
            pagination={pagination}
            filters={filters}
            sort={sort}
            actions={actions}
            rowKey="id"
            onSort={setSort}
            onFilter={setFilters}
            onPageChange={(page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }))
              fetchSaleShops({
                page,
                pageSize,
                sort,
                filters,
                search: '',
              })
            }}
            onSearch={search => {
              fetchSaleShops({
                page: 1,
                pageSize: pagination.pageSize,
                sort,
                filters,
                search,
              })
            }}
            onServerSideChange={fetchSaleShops}
            serverSide={true}
            searchable={true}
            searchPlaceholder="Search by name, description, or URL..."
            emptyText="No sales channels found"
            emptyIcon={<ShoppingCart className="w-12 h-12" style={{ color: branding.textSecondaryColor }} />}
            loadingText="Loading sales channels..."
            showStats={true}
            showSearch={true}
            showFilters={true}
            showPagination={true}
            showActions={true}
            striped={true}
            hoverable={true}
            bordered={false}
            className="!bg-transparent !border-none !rounded-none"
          />
        </div>
      </div>
    </div>
  )
}
